import { useCallback, useRef } from "react";
import styles from "./ExportControls.module.css";
import { PRESET_GRADIENTS, TEMPLATES } from "./ScreenshotTemplates.tsx";
import type { ScreenshotTemplate } from "./ScreenshotTemplates.tsx";
import type { DeviceSpec } from "./devices.ts";
import { DEVICES } from "./devices.ts";
import { batchExport, exportScreenshot } from "./renderScreenshot.ts";

interface ExportControlsProps {
  selectedDevice: DeviceSpec;
  onDeviceChange: (device: DeviceSpec) => void;
  selectedTemplate: ScreenshotTemplate;
  onTemplateChange: (template: ScreenshotTemplate) => void;
  headline: string;
  onHeadlineChange: (value: string) => void;
  subtitle: string;
  onSubtitleChange: (value: string) => void;
  screenshotImage: HTMLImageElement | null;
  onImageChange: (image: HTMLImageElement | null) => void;
}

export function ExportControls({
  selectedDevice,
  onDeviceChange,
  selectedTemplate,
  onTemplateChange,
  headline,
  onHeadlineChange,
  subtitle,
  onSubtitleChange,
  screenshotImage,
  onImageChange,
}: ExportControlsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDeviceChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const device = DEVICES.find((d) => d.id === e.target.value);
      if (device) onDeviceChange(device);
    },
    [onDeviceChange],
  );

  const handleTemplateChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const template = TEMPLATES.find((t) => t.id === e.target.value);
      if (template) {
        onTemplateChange(template);
        onHeadlineChange(template.headline);
        onSubtitleChange(template.subtitle);
      }
    },
    [onTemplateChange, onHeadlineChange, onSubtitleChange],
  );

  const handleGradientSelect = useCallback(
    (gradientValue: string) => {
      onTemplateChange({
        ...selectedTemplate,
        gradient: gradientValue,
      });
    },
    [selectedTemplate, onTemplateChange],
  );

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const img = new Image();
      img.onload = () => {
        onImageChange(img);
      };
      img.src = URL.createObjectURL(file);
    },
    [onImageChange],
  );

  const handleClearImage = useCallback(() => {
    onImageChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [onImageChange]);

  const handleExportPng = useCallback(() => {
    exportScreenshot(selectedDevice, selectedTemplate, headline, subtitle, screenshotImage);
  }, [selectedDevice, selectedTemplate, headline, subtitle, screenshotImage]);

  const handleBatchExport = useCallback(() => {
    batchExport(DEVICES, selectedTemplate, headline, subtitle, screenshotImage);
  }, [selectedTemplate, headline, subtitle, screenshotImage]);

  return (
    <div className={styles.controls}>
      <h2 className={styles.title}>Controls</h2>

      {/* Device Selector */}
      <div className={styles.field}>
        <label className={styles.label} htmlFor="device-select">
          Device
        </label>
        <select
          id="device-select"
          className={styles.select}
          value={selectedDevice.id}
          onChange={handleDeviceChange}
        >
          <optgroup label="iPhone">
            {DEVICES.filter((d) => d.category === "iphone").map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} {d.required ? "(Required)" : ""}
              </option>
            ))}
          </optgroup>
          <optgroup label="iPad">
            {DEVICES.filter((d) => d.category === "ipad").map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </optgroup>
          <optgroup label="Apple Watch">
            {DEVICES.filter((d) => d.category === "watch").map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} {d.required ? "(Required)" : ""}
              </option>
            ))}
          </optgroup>
        </select>
      </div>

      {/* Template Selector */}
      <div className={styles.field}>
        <label className={styles.label} htmlFor="template-select">
          Template
        </label>
        <select
          id="template-select"
          className={styles.select}
          value={selectedTemplate.id}
          onChange={handleTemplateChange}
        >
          <optgroup label="Classic">
            {TEMPLATES.filter((t) => t.app === "classic").map((t) => (
              <option key={t.id} value={t.id}>
                {t.headline.replace("\n", " ")}
              </option>
            ))}
          </optgroup>
          <optgroup label="AI">
            {TEMPLATES.filter((t) => t.app === "ai").map((t) => (
              <option key={t.id} value={t.id}>
                {t.headline.replace("\n", " ")}
              </option>
            ))}
          </optgroup>
        </select>
      </div>

      {/* Headline */}
      <div className={styles.field}>
        <label className={styles.label} htmlFor="headline-input">
          Headline
        </label>
        <textarea
          id="headline-input"
          className={styles.textarea}
          value={headline}
          onChange={(e) => onHeadlineChange(e.target.value)}
          rows={2}
          placeholder="Enter headline text (use \n for line break)"
        />
      </div>

      {/* Subtitle */}
      <div className={styles.field}>
        <label className={styles.label} htmlFor="subtitle-input">
          Subtitle
        </label>
        <input
          id="subtitle-input"
          className={styles.input}
          type="text"
          value={subtitle}
          onChange={(e) => onSubtitleChange(e.target.value)}
          placeholder="Enter subtitle text"
        />
      </div>

      {/* Gradient Picker */}
      <div className={styles.field}>
        <span className={styles.label}>Background Gradient</span>
        <div className={styles.gradientGrid}>
          {PRESET_GRADIENTS.map((g) => (
            <button
              type="button"
              key={g.id}
              className={`${styles.gradientSwatch} ${
                selectedTemplate.gradient === g.value ? styles.gradientSwatchActive : ""
              }`}
              style={{ background: g.value }}
              onClick={() => handleGradientSelect(g.value)}
              title={g.label}
            />
          ))}
        </div>
      </div>

      {/* Screenshot Upload */}
      <div className={styles.field}>
        <span className={styles.label}>Screenshot Image</span>
        <div className={styles.uploadRow}>
          <button
            type="button"
            className={styles.uploadButton}
            onClick={() => fileInputRef.current?.click()}
          >
            {screenshotImage ? "Replace Image" : "Upload Screenshot"}
          </button>
          {screenshotImage && (
            <button type="button" className={styles.clearButton} onClick={handleClearImage}>
              Clear
            </button>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileUpload}
          className={styles.fileInput}
        />
      </div>

      {/* Export Buttons */}
      <div className={styles.exportSection}>
        <button type="button" className={styles.exportButton} onClick={handleExportPng}>
          Export PNG
        </button>
        <button type="button" className={styles.batchButton} onClick={handleBatchExport}>
          Batch Export (Required Devices)
        </button>
      </div>
    </div>
  );
}
